import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getAuth } from 'firebase/auth';
import { StripeCheckoutSession, UserSubscription, PricingTier, BillingCycle } from '../types';
import { isPrerendering } from './prerender';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// Lazy Stripe loader that is safe during prerender
let stripePromise: Promise<Stripe | null> | null = null;

function getStripe(): Promise<Stripe | null> {
  if (isPrerendering()) {
    return Promise.resolve(null);
  }

  if (!stripePromise) {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      console.warn('[subscriptionApi] REACT_APP_STRIPE_PUBLISHABLE_KEY is not set; Stripe will be unavailable.');
      return Promise.resolve(null);
    }

    stripePromise = loadStripe(publishableKey);
  }

  return stripePromise;
}

// Helper function to get auth headers
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      return {};
    }
    
    const token = await user.getIdToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  } catch (error) {
    console.error('Error getting auth headers:', error);
    return {};
  }
};

export interface SubscriptionConfig {
  priceConfig: Record<string, {
    tier: PricingTier;
    billingCycle: BillingCycle;
  }>;
  tiers: PricingTier[];
  billingCycles: BillingCycle[];
}

export interface CurrentSubscription {
  subscription: {
    id: string;
    status: string;
    tier: PricingTier;
    billingCycle: BillingCycle;
    currentPeriodStart: Date | null;
    currentPeriodEnd: Date | null;
    nextBillingDate: Date | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  tier: PricingTier;
}

/**
 * Get pricing configuration from backend
 */
export async function getSubscriptionConfig(): Promise<SubscriptionConfig> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/subscriptions/config`);
    
    if (!response.ok) {
      throw new Error(`Failed to get subscription config: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting subscription config:', error);
    throw error;
  }
}

/**
 * Create a checkout session and redirect to Stripe
 */
export async function createCheckoutSession(
  tier: PricingTier,
  billingCycle: BillingCycle,
  successUrl: string = `${window.location.origin}/subscription/success`,
  cancelUrl: string = `${window.location.origin}/subscription/cancel`
): Promise<void> {
  try {
    const authHeaders = await getAuthHeaders();
    
    // Determine price key based on tier and billing cycle
    const priceKey = getPriceKey(tier, billingCycle);
    
    const response = await fetch(`${BACKEND_URL}/api/subscriptions/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        priceKey,
        successUrl,
        cancelUrl,
      }),
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        // If we can't parse JSON, use the status text
      }
      console.error('Backend error response:', errorMessage);
      throw new Error(errorMessage);
    }
    
    const { sessionId, url }: StripeCheckoutSession = await response.json();
    
    // Redirect to Stripe Checkout
    if (url) {
      window.location.href = url;
    } else {
      // Fallback to using Stripe.js
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe failed to load or is not available');
      }
      
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        throw new Error(error.message);
      }
    }
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
}

/**
 * Get all user subscriptions (active and inactive)
 */
export async function getAllSubscriptions(): Promise<{
  subscriptions: Array<{
    id: string;
    tier: string;
    status: string;
    billingCycle: string;
    startDate: string | null;
    nextBillingDate: string | null;
    price: string;
    isActive: boolean;
    cancelAtPeriodEnd: boolean;
    createdAt: string | null;
    canceledAt: string | null;
    endedAt: string | null;
  }>;
}> {
  try {
    const authHeaders = await getAuthHeaders();
    
    const response = await fetch(`${BACKEND_URL}/api/subscriptions/all`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get subscriptions: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    return data;
  } catch (error) {
    console.error('❌ [subscriptionApi] Error getting all subscriptions:', error);
    throw error;
  }
}

/**
 * Get user's payment history with invoice URLs
 */
export async function getPaymentHistory(): Promise<{
  payments: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    date: string;
    description: string;
    invoiceUrl: string | null;
    subscriptionId: string | null;
    chargeId: string | null;
  }>;
}> {
  try {
    const authHeaders = await getAuthHeaders();
    
    const response = await fetch(`${BACKEND_URL}/api/subscriptions/payment-history`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get payment history: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    return data;
  } catch (error) {
    console.error('❌ [subscriptionApi] Error getting payment history:', error);
    throw error;
  }
}

/**
 * Get current user's subscription
 */
export async function getCurrentSubscription(): Promise<CurrentSubscription> {
  try {
    if (isPrerendering()) {
      // During prerender, return a safe default without hitting backend
      return {
        subscription: null,
        tier: PricingTier.Free,
      };
    }
    const authHeaders = await getAuthHeaders();
    
    const response = await fetch(`${BACKEND_URL}/api/subscriptions/current`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get current subscription: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Convert date strings to Date objects
    if (data.subscription) {
      
      data.subscription.currentPeriodStart = data.subscription.currentPeriodStart ? new Date(data.subscription.currentPeriodStart) : null;
      data.subscription.currentPeriodEnd = data.subscription.currentPeriodEnd ? new Date(data.subscription.currentPeriodEnd) : null;
      data.subscription.nextBillingDate = data.subscription.nextBillingDate ? new Date(data.subscription.nextBillingDate) : null;
    }
    
    return data;
  } catch (error) {
    console.error('❌ [subscriptionApi] Error getting current subscription:', error);
    throw error;
  }
}

/**
 * Cancel current subscription
 */
export async function cancelSubscription(cancelAtPeriodEnd: boolean = true): Promise<void> {
  try {
    const authHeaders = await getAuthHeaders();
    
    const response = await fetch(`${BACKEND_URL}/api/subscriptions/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        cancelAtPeriodEnd,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
}

/**
 * Reactivate a canceled subscription
 */
export async function reactivateSubscription(): Promise<void> {
  try {
    const authHeaders = await getAuthHeaders();
    
    const response = await fetch(`${BACKEND_URL}/api/subscriptions/reactivate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error reactivating subscription:', error);
    throw error;
  }
}

/**
 * Get price key for a given tier and billing cycle
 */
export function getPriceKey(tier: PricingTier, billingCycle: BillingCycle): string {
  const tierKey = tier.toLowerCase();
  const cycleKey = billingCycle.toLowerCase() === 'monthly' ? 'monthly' : 'yearly';
  return `${tierKey}_${cycleKey}`;
}

/**
 * Safely create checkout session with enhanced error handling
 * This is used for both direct calls and URL parameter-based flows
 */
export async function createCheckoutSessionSafely(
  tier: PricingTier,
  billingCycle: BillingCycle,
  successUrl?: string,
  cancelUrl?: string
): Promise<void> {
  try {
    // Validate inputs
    if (!tier || !billingCycle) {
      throw new Error('Invalid tier or billing cycle provided');
    }

    // Validate tier is not Free (Free plans don't need checkout)
    if (tier === PricingTier.Free) {
      throw new Error('Free plans do not require checkout');
    }

    // Check if user already has an active subscription
    const currentSubscription = await getCurrentSubscription();
    
    if (currentSubscription.subscription && ['active', 'trialing'].includes(currentSubscription.subscription.status)) {
      throw new Error(`You already have an active ${currentSubscription.subscription.tier} subscription. Please manage your existing subscription instead.`);
    }
    
    await createCheckoutSession(tier, billingCycle, successUrl, cancelUrl);
  } catch (error) {
    console.error('❌ [subscriptionApi] Error in createCheckoutSessionSafely:', error);
    throw error;
  }
}

/**
 * Format subscription status for display
 */
export function formatSubscriptionStatus(status: string): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'trialing':
      return 'Trial';
    case 'past_due':
      return 'Past Due';
    case 'canceled':
      return 'Canceled';
    case 'unpaid':
      return 'Unpaid';
    case 'incomplete':
      return 'Incomplete';
    case 'incomplete_expired':
      return 'Expired';
    default:
      return status;
  }
}

/**
 * Check if subscription is active (active or trialing)
 */
export function isSubscriptionActive(status: string): boolean {
  return ['active', 'trialing'].includes(status);
}

/**
 * Get tier color for UI display
 */
export function getTierColor(tier: PricingTier): string {
  switch (tier) {
    case PricingTier.Free:
      return 'text-gray-600 bg-gray-100';
    case PricingTier.Basic:
      return 'text-blue-600 bg-blue-100';
    case PricingTier.Pro:
      return 'text-emerald-600 bg-emerald-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

/**
 * Migrate user's quota to ThreadIt structure
 */
export async function migrateQuotas(aggregate: boolean = true): Promise<{ success: boolean; message: string; quota?: any; tier?: string }> {
  try {
    const authHeaders = await getAuthHeaders();
    
    const url = new URL(`${BACKEND_URL}/api/subscriptions/migrate-quotas`);
    if (!aggregate) {
      url.searchParams.set('aggregate', 'false');
    }
    
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to migrate quotas');
    }

    return data;
  } catch (error) {
    console.error('❌ [subscriptionApi] Error migrating quotas:', error);
    throw error;
  }
}

/**
 * Check if user's quota is migrated to ThreadIt structure (without aggregation)
 */
export async function checkQuotaMigration(): Promise<{ success: boolean; message: string; quota?: any; tier?: string }> {
  return migrateQuotas(false);
}
